import type { ExerciseTypeConfig } from '../registry/types';
import type { 
  ScrambleExerciseDefinition, 
  ScrambleExerciseSubmission,
  ScrambleEvaluationDetail 
} from '../types';
import { scrambleSubmissionSchema } from '../types/schemas';
import { normalizeAnswer as normalizeText } from '../text/normalize';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: ScrambleExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * Split a sentence into words, normalizing whitespace
 */
function splitSentenceIntoWords(sentence: string): string[] {
  return sentence.trim().split(/\s+/).filter(word => word.length > 0);
}

/**
 * Normalize a word for comparison. Shares the platform's text normalization
 * (quote/dash glyphs, diacritics, whitespace, case) so a curly vs. straight
 * apostrophe doesn't fail a match — but keeps contractions intact, since each
 * token is matched positionally and "don't" must stay a single word.
 */
function normalizeWord(word: string): string {
  return normalizeText(word, { expandContractions: false });
}

/**
 * Scramble exercise type configuration
 */
export const scrambleConfig: ExerciseTypeConfig<
  'Scramble',
  ScrambleExerciseDefinition,
  ScrambleExerciseSubmission,
  ScrambleEvaluationDetail
> = {
  type: 'Scramble',
  isAutoGradable: true,
  submissionSchema: scrambleSubmissionSchema,

  defaultMaxScore: (definition) => {
    // Total words across all sentences
    const totalWords = definition.sentences?.reduce((count, sentence) => {
      const words = splitSentenceIntoWords(sentence.correctSentence);
      return count + words.length;
    }, 0) ?? 0;
    return getNormalizedMaxScore(definition, Math.max(1, totalWords));
  },

  evaluate: (definition, submission) => {
    const sentences = definition.sentences ?? [];
    const userAnswers = submission.answers ?? [];

    let totalWords = 0;
    let totalCorrect = 0;

    const sentenceEvaluations = sentences.map((sentence, sentenceIndex) => {
      const correctWords = splitSentenceIntoWords(sentence.correctSentence);
      const providedWords = userAnswers[sentenceIndex] ?? [];

      const wordEvaluations = correctWords.map((expectedWord, position) => {
        const providedWord = providedWords[position] ?? '';
        const isCorrect = normalizeWord(expectedWord) === normalizeWord(providedWord);
        
        if (isCorrect) {
          totalCorrect++;
        }
        totalWords++;

        return {
          position,
          expectedWord,
          providedWord,
          isCorrect,
        };
      });

      const isFullyCorrect = wordEvaluations.every(w => w.isCorrect);

      return {
        sentenceId: sentence.sentenceId,
        correctWords,
        providedWords,
        wordEvaluations,
        isFullyCorrect,
      };
    });

    const maxScore = getNormalizedMaxScore(definition, Math.max(1, totalWords));

    return {
      score: totalWords === 0 ? 0 : Math.round((totalCorrect / totalWords) * maxScore),
      maxScore,
      isFullyCorrect: totalWords > 0 && totalCorrect === totalWords,
      details: {
        kind: 'Scramble',
        sentenceEvaluations,
      },
    };
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    const sentences = Array.isArray(details.sentences) ? details.sentences : [];

    return {
      sentences: sentences.map((sentence: any, index: number) => ({
        sentenceId: sentence?.sentenceId,
        correctSentence: sentence?.correctSentence ?? '',
        order: sentence?.order ?? index,
        correction: sentence?.correction ?? '',
      })),
    };
  },

  createDefault: (order) => ({
    type: 'Scramble' as const,
    sentences: [
      {
        sentenceId: undefined,
        correctSentence: '',
        order: 0,
        correction: '',
      },
    ],
  }),
};
